import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Center,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useCreateNotification } from "hooks/notifications/useCreateNotification";
import { useAppTranslation } from "hooks/useTranslation";
import { TranslateButton } from "ui";

type SendPushToBookmarksProps = {
  visible: boolean;
  eventId: number;
  onClose: () => void;
  /** Drafted messages (Phase 33 desk, pushDrafts.ts); seeded when the modal opens. */
  initialEnglish?: string;
  initialPortuguese?: string;
  /** Renders "Skip the push"; in desk mode closing by the overlay also skips. */
  onSkip?: () => void;
  /** Desk mode: called after a successful send instead of the "Sent" modal. */
  onSent?: () => void;
  /**
   * Desk mode (D-15): the modal ends through onSkip or onSent, and a green
   * notification replaces the "Sent" modal. Without it the flow is unchanged.
   */
  desk?: boolean;
};

export const SendPushToBookmarks = (props: SendPushToBookmarksProps) => {
  const {
    visible,
    eventId,
    onClose,
    initialEnglish,
    initialPortuguese,
    onSkip,
    onSent,
    desk,
  } = props;

  const { t } = useAppTranslation();

  const [askAgain, setAskAgain] = useState(false);
  const [sent, setSent] = useState(false);
  // In-flight guard (review AD-WR-03): each "Yes" inserts a notifications
  // row and every row is a push to every bookmarker, so a second click must
  // never send again. The ref is set synchronously, before the first await;
  // the state drives the busy button.
  const sendingRef = useRef(false);
  const [sending, setSending] = useState(false);

  const createNotification = useCreateNotification();

  const form = useForm({
    initialValues: {
      english: initialEnglish ?? "",
      portuguese: initialPortuguese ?? "",
    },
  });

  // Drafts can arrive after mount (the desk opens the modal after a save), so
  // they are re-seeded each time the modal opens with drafts.
  useEffect(() => {
    if (!visible) return;
    if (initialEnglish === undefined && initialPortuguese === undefined) return;
    const drafts = {
      english: initialEnglish ?? "",
      portuguese: initialPortuguese ?? "",
    };
    form.setInitialValues(drafts);
    form.setValues(drafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialEnglish, initialPortuguese]);

  const formSubmit = async (values: any) => {
    setAskAgain(true);
  };

  const sendPush = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      await createNotification.mutateAsync({
        eventId,
        message: form.values.english,
        message_pt: form.values.portuguese,
      });
    } catch {
      // A push that did not go out is never shown as sent: back to the draft
      // (her text kept) so she can retry or skip, with the failure said aloud.
      sendingRef.current = false;
      setSending(false);
      setAskAgain(false);
      notifications.show({ color: "red", message: t("push.send_failed") });
      return;
    }
    sendingRef.current = false;
    setSending(false);
    setAskAgain(false);
    if (desk) {
      form.reset();
      notifications.show({ color: "green", message: t("push.sent") });
      if (onSent) {
        onSent();
      } else {
        onClose();
      }
      return;
    }
    closeDialog();
    setSent(true);
  };

  const closeDialog = () => {
    form.reset();
    onClose();
  };

  const skip = () => {
    form.reset();
    onSkip?.();
  };

  return (
    <>
      <Modal
        opened={visible}
        centered
        withCloseButton={false}
        onClose={desk && onSkip ? skip : closeDialog}
        size="xl"
        radius="md"
        style={{ flex: 1 }}
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <form onSubmit={form.onSubmit(formSubmit)}>
          <Text size="xl">{t("push.title")}</Text>
          {onSkip && (
            <Text size="sm" c="dimmed" mt="xs">
              {t("push.body")}
            </Text>
          )}
          <TextInput
            mt="md"
            label={t("push.english")}
            {...form.getInputProps("english")}
            data-autofocus
            rightSection={
              <TranslateButton
                originalText={form.values.portuguese}
                tragetLang="en"
                onTranslated={(translatedText) => {
                  form.setFieldValue("english", translatedText);
                }}
              />
            }
          />
          <TextInput
            mt="md"
            label={t("push.portuguese")}
            {...form.getInputProps("portuguese")}
            rightSection={
              <TranslateButton
                originalText={form.values.english}
                tragetLang="pt"
                onTranslated={(translatedText) => {
                  form.setFieldValue("portuguese", translatedText);
                }}
              />
            }
          />
          <Group mt="md">
            <Button
              type="submit"
              disabled={
                form.values.english === "" || form.values.portuguese === ""
              }
            >
              {t("push.send")}
            </Button>
            {onSkip && (
              <Button variant="default" onClick={skip}>
                {t("push.skip")}
              </Button>
            )}
          </Group>
        </form>
      </Modal>
      <Modal
        opened={askAgain}
        centered
        onClose={() => {
          if (!sendingRef.current) setAskAgain(false);
        }}
        withCloseButton={false}
      >
        <Alert variant="light" color="gray" title={t("push.confirm_title")}>
          <Text>{t("push.confirm_body")}</Text>
          <Group mt="md">
            <Button variant="filled" onClick={sendPush} loading={sending}>
              {t("push.confirm_yes")}
            </Button>
            <Button
              variant="default"
              disabled={sending}
              onClick={() => {
                setAskAgain(false);
              }}
            >
              {t("push.confirm_no")}
            </Button>
          </Group>
        </Alert>
      </Modal>
      <Modal
        opened={sent}
        centered
        onClose={() => {
          setSent(false);
        }}
        size="md"
        withCloseButton={false}
      >
        <Center>
          <Stack align="center">
            <Text size="xl">{t("push.sent")}</Text>
            <Button
              onClick={() => {
                setSent(false);
              }}
            >
              {t("push.sent_close")}
            </Button>
          </Stack>
        </Center>
      </Modal>
    </>
  );
};
